// Pure theme logic for #215 light mode. No React, no DOM at import time — every
// side-effecting dependency (storage, media query, root element) is injected so
// the whole module is unit + mutation testable. The React ThemeProvider and the
// pre-paint inline script in index.html are thin wrappers over these primitives.
import {
  meetsWcagAa,
  type Rgb,
} from "@/lib/catalog-pending/visual-a11y/contrastCalculator";
import type { Theme, ThemePreference } from "@/lib/store/ui-store";

export const STORAGE_KEY = "quartel-theme";
export const THEME_COLORS: Record<Theme, string> = {
  dark: "#0A0A0A",
  light: "#F0F7FF",
};

/** Minimal slice of the Web Storage API so tests can inject an in-memory fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Minimal slice of MediaQueryList so tests never touch real `window.matchMedia`. */
export interface MediaQueryLike {
  readonly matches: boolean;
}

/**
 * The system-preference media query. We query `prefers-color-scheme: light`
 * (NOT dark) on purpose: jsdom/Playwright default `matchMedia(...).matches` to
 * `false`, so unmatched → "dark". This keeps the product's dark-primary default
 * and every dark visual baseline byte-stable, while genuine light-OS users
 * (matches === true) still get light. See [[lightmode-desktop-tracks]].
 */
export const SYSTEM_LIGHT_QUERY = "(prefers-color-scheme: light)";

function isThemePreference(value: string | null): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

/** Read + validate the persisted preference. Returns null on miss/garbage/throw. */
export function readStoredThemePreference(
  storage: StorageLike,
): ThemePreference | null {
  try {
    const stored = storage.getItem(STORAGE_KEY);
    return isThemePreference(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Back-compat alias for older tests/imports while #215 migrates terminology. */
export const readStoredTheme = readStoredThemePreference;

/** Map the `prefers-color-scheme: light` match to a theme. */
export function systemPreference(mql: MediaQueryLike): Theme {
  return mql.matches ? "light" : "dark";
}

/**
 * Precedence: explicit stored choice > dark. Operador 08/07/2026 (bug de
 * produção): usuário novo abria no claro porque o fallback consultava
 * `prefers-color-scheme` — o default do produto é SEMPRE dark; o sistema
 * operacional não opina. `system` é aceito só por compat de assinatura.
 */
export function resolveTheme(
  stored: ThemePreference | null,
  system: Theme | null = null,
): Theme {
  void system;
  if (stored === "light" || stored === "dark") return stored;
  return "dark";
}

/** Persist the explicit choice. Swallows quota / private-mode failures. */
export function persistThemePreference(
  storage: StorageLike,
  preference: ThemePreference,
): void {
  try {
    storage.setItem(STORAGE_KEY, preference);
  } catch {
    // storage quota exceeded or private-browsing restriction — ignore
  }
}

/** Back-compat alias for older tests/imports while #215 migrates terminology. */
export const persistTheme = persistThemePreference;

function updateThemeColor(root: HTMLElement, theme: Theme): void {
  const meta = root.ownerDocument.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  meta?.setAttribute("content", THEME_COLORS[theme]);
}

function forceTheme(root: HTMLElement, theme: Theme): void {
  root.classList.toggle("dark", theme === "dark");
  root.setAttribute("data-theme", theme);
  root.style.colorScheme = theme;
  updateThemeColor(root, theme);
}

/**
 * Apply a theme to the document root. Sets `data-theme` (activates the light
 * override layer), toggles the `.dark` class (drives the existing dark token
 * cascade — never removed for dark), and sets `color-scheme` for native controls.
 * While the admin dark lock is held every request is coerced to dark — see
 * lockAdminThemeDark below.
 */
export function applyTheme(root: HTMLElement, theme: Theme): void {
  forceTheme(root, adminThemeLocked ? "dark" : theme);
}

// --- Admin forced-dark lock --------------------------------------------------
// Operator mandate (#215 follow-up): the /admin backoffice keeps the legacy
// dark chrome byte-identical, ALWAYS — even with quartel-theme=light persisted.
// AdminLayout acquires the lock while mounted; because applyTheme() coerces to
// dark while it is held, the ThemeProvider (hydration, system-preference
// listener) and the client ThemeToggle can re-run freely without ever
// repainting the admin shell light. The user's preference keeps being resolved
// and persisted normally — only the DOM is pinned.
let adminThemeLocked = false;

/** True while AdminLayout pins the document to the legacy dark chrome. */
export function isAdminThemeLocked(): boolean {
  return adminThemeLocked;
}

/**
 * Acquire the lock and force the document root to dark.
 * Usage: `lockAdminThemeDark(document.documentElement)` in a layout effect.
 */
export function lockAdminThemeDark(root: HTMLElement): void {
  adminThemeLocked = true;
  forceTheme(root, "dark");
}

/**
 * Release the lock and apply the user's resolved preference.
 * Usage: `unlockAdminTheme(document.documentElement, uiStore.theme)` on unmount.
 */
export function unlockAdminTheme(root: HTMLElement, restored: Theme): void {
  adminThemeLocked = false;
  forceTheme(root, restored);
}

/** WCAG AA guard reused by the contrast E2E/unit twins. */
export function contrastGuard(fg: Rgb, bg: Rgb): boolean {
  return meetsWcagAa(fg, bg);
}

/**
 * The exact pre-paint IIFE inlined into index.html's <head>. Single source of
 * truth: a unit test asserts index.html contains this string so the script and
 * the React provider can never drift. Kept on one line so the assertion is
 * robust to surrounding HTML indentation.
 *
 * The `/admin` pathname check mirrors the AdminLayout dark lock: direct loads
 * of the backoffice must paint dark before the React bundle mounts, even with
 * quartel-theme=light persisted (operator mandate, #215 follow-up).
 */
export function noFlashSnippet(): string {
  return `(function(){try{var k="${STORAGE_KEY}";var s=localStorage.getItem(k);var t=(s==="light")?"light":"dark";var p=location.pathname;if(p==="/admin"||p.indexOf("/admin/")===0){t="dark";}var r=document.documentElement;r.setAttribute("data-theme",t);r.style.colorScheme=t;if(t==="dark"){r.classList.add("dark");}else{r.classList.remove("dark");}var m=document.querySelector('meta[name="theme-color"]');if(m){m.setAttribute("content",t==="dark"?"${THEME_COLORS.dark}":"${THEME_COLORS.light}");}}catch(e){var d=document.documentElement;d.setAttribute("data-theme","dark");d.classList.add("dark");}})();`;
}
